const INTERNAL_ORIGIN = 'https://powston.invalid';
const PENDING_LOGIN_NEXT_KEY = 'powston.pending-login-next';
const PENDING_LOGIN_MAX_AGE_MS = 15 * 60 * 1000;

function browserSessionStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Accept only application-local routes so `next` cannot be an open redirect. */
export function validatedNextPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  if (/[\\\u0000-\u001f\u007f]/.test(value)) return null;

  try {
    const decoded = decodeURIComponent(value);
    if (decoded.startsWith('//') || /[\\\u0000-\u001f\u007f]/.test(decoded)) return null;
    const parsed = new URL(value, INTERNAL_ORIGIN);
    if (parsed.origin !== INTERNAL_ORIGIN) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function emailRedirectTarget(requestedNext: string | null | undefined): string | undefined {
  return validatedNextPath(requestedNext) ?? undefined;
}

export function currentRoute(location: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  return validatedNextPath(`${location.pathname}${location.search}${location.hash}`) ?? '/';
}

export function loginRedirectFor(route: string): string {
  const next = validatedNextPath(route);
  return next ? `/login?next=${encodeURIComponent(next)}` : '/login';
}

export type LoginMethod = 'google' | 'github' | 'tesla' | 'passkey';

const LOGIN_METHOD_PATHS: Record<LoginMethod, string> = {
  google: '/api/login/google',
  github: '/api/login/github',
  tesla: '/api/login/tesla',
  passkey: '/passkey',
};

export function loginMethodHref(method: LoginMethod, requestedNext: string | null | undefined): string {
  const base = LOGIN_METHOD_PATHS[method];
  const next = validatedNextPath(requestedNext);
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

export function clearPendingLoginNext(
  storage: Pick<Storage, 'removeItem'> | undefined = browserSessionStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(PENDING_LOGIN_NEXT_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted contexts.
  }
}

/**
 * OAuth currently returns to `/`; retain the validated route in this tab so
 * UserProvider can finish the redirect after the callback establishes a user.
 */
export function rememberLoginNext(
  requestedNext: string | null | undefined,
  storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined = browserSessionStorage(),
  now = Date.now(),
): void {
  const next = validatedNextPath(requestedNext);
  if (!storage) return;
  try {
    if (!next) {
      clearPendingLoginNext(storage);
      return;
    }
    storage.setItem(PENDING_LOGIN_NEXT_KEY, JSON.stringify({ next, createdAt: now }));
  } catch {
    // Storage can be unavailable in privacy-restricted contexts. The backend
    // still receives `next`, so authentication remains usable.
  }
}

export function consumeLoginNext(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | undefined = browserSessionStorage(),
  now = Date.now(),
): string | null {
  if (!storage) return null;
  let stored: string | null;
  try {
    stored = storage.getItem(PENDING_LOGIN_NEXT_KEY);
    storage.removeItem(PENDING_LOGIN_NEXT_KEY);
  } catch {
    return null;
  }
  if (!stored) return null;

  try {
    const value = JSON.parse(stored) as { next?: unknown; createdAt?: unknown };
    if (typeof value.createdAt !== 'number' || now - value.createdAt > PENDING_LOGIN_MAX_AGE_MS) return null;
    return typeof value.next === 'string' ? validatedNextPath(value.next) : null;
  } catch {
    return null;
  }
}

export class UserSessionRequestError extends Error {
  constructor(public readonly status: number) {
    super(`/api/user returned ${status}`);
    this.name = 'UserSessionRequestError';
  }
}

export function isExpiredSessionError(error: unknown): boolean {
  return error instanceof UserSessionRequestError && [401, 403].includes(error.status);
}

export function isTransientSessionError(error: unknown): boolean {
  if (!(error instanceof UserSessionRequestError)) return true;
  return error.status === 408 || error.status === 429 || error.status >= 500;
}

type RetryOptions = {
  attempts?: number;
  delayMs?: number;
  wait?: (milliseconds: number) => Promise<void>;
};

/** Retry only failures that may clear without changing the user's session. */
export async function fetchUserWithRetry<T>(
  fetchUser: () => Promise<T>,
  { attempts = 3, delayMs = 300, wait = (ms) => new Promise(resolve => setTimeout(resolve, ms)) }: RetryOptions = {},
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await fetchUser();
    } catch (error) {
      lastError = error;
      if (!isTransientSessionError(error) || attempt === attempts) throw error;
      await wait(delayMs * attempt);
    }
  }
  throw lastError;
}
